/**
 * Small pure-geometry helpers for the raw converter: polygon areas, bboxes,
 * boolean ops (thin wrappers over polygon-clipping), and cleaned wall segments.
 */

import polygonClipping, { type MultiPolygon as PcMultiPolygon } from 'polygon-clipping';
import { ringSignedArea, toPcRing } from '../geometry';
import { Point } from '../schemas';

/** A cleaned wall segment in the working (feet, y-up raw) frame. */
export interface CleanWall {
  id: string;
  a: Point;
  b: Point;
  /** "h" = axis-aligned horizontal, "v" = vertical, "d" = genuine diagonal. */
  axis: 'h' | 'v' | 'd';
  length: number;
  /** True for gap-bridging walls the converter synthesized (no scan evidence). */
  synthetic?: boolean;
}

export interface BBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const bboxOf = (pts: readonly Point[]): BBox => ({
  x0: Math.min(...pts.map(p => p.x)),
  y0: Math.min(...pts.map(p => p.y)),
  x1: Math.max(...pts.map(p => p.x)),
  y1: Math.max(...pts.map(p => p.y)),
});

export const dist = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);

export const rectRingPts = (b: BBox): Point[] => [
  { x: b.x0, y: b.y0 },
  { x: b.x1, y: b.y0 },
  { x: b.x1, y: b.y1 },
  { x: b.x0, y: b.y1 },
];

/** Total unsigned area of a multipolygon (holes subtract). */
export function mpArea(mp: PcMultiPolygon): number {
  let sum = 0;
  for (const poly of mp) {
    for (let i = 0; i < poly.length; i++) {
      const ring = poly[i].map(([x, y]) => ({ x, y }));
      const a = Math.abs(ringSignedArea(ring));
      sum += i === 0 ? a : -a;
    }
  }
  return sum;
}

export const toMp = (ring: Point[]): PcMultiPolygon => [[toPcRing(ring)]];

export const intersectArea = (a: Point[], b: Point[]): number => mpArea(polygonClipping.intersection(toMp(a), toMp(b)));

/** a − b as a list of outer rings (one per connected component; holes reported separately). */
export function differenceComponents(a: Point[], b: Point[]): { ring: Point[]; area: number }[] {
  const mp = polygonClipping.difference(toMp(a), toMp(b));
  return mp.map(poly => {
    const ring = poly[0].map(([x, y]) => ({ x, y }));
    // drop the closing duplicate vertex polygon-clipping emits
    if (ring.length > 1 && dist(ring[0], ring[ring.length - 1]) < 1e-9) ring.pop();
    return { ring, area: Math.abs(ringSignedArea(ring)) };
  });
}

/** Remove collinear / coincident vertices from a ring (tolerance in output units). */
export function simplifyRing(ring: Point[], tol = 1e-4): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < ring.length; i++) {
    const prev = ring[(i + ring.length - 1) % ring.length];
    const cur = ring[i];
    const next = ring[(i + 1) % ring.length];
    if (dist(prev, cur) < tol) continue;
    const cross = (cur.x - prev.x) * (next.y - cur.y) - (cur.y - prev.y) * (next.x - cur.x);
    const collinear = Math.abs(cross) < tol * Math.max(dist(prev, cur), dist(cur, next));
    if (!collinear) out.push(cur);
  }
  return out;
}
